// formData.ts

export enum CompanyType {
  MEI = "MEI",
  LIMITED = "LIMITED",
  INDIVIDUAL = "INDIVIDUAL",
}

export interface ActivationFormData {
  document: string;
  name: string;
  email: string;
  mobilePhone: string;
  birthDate?: string | null;
  companyType: CompanyType | "";
  incomeValue: number;
  address: string;
  addressNumber: string;
  complement?: string;
  province: string;
  zipCode: string;
}

export enum Talk2PayStatus {
  NOT_SENT = "NOT_SENT",
  SENT = "SENT",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  PENDING = "PENDING",
  AWAITING_APPROVAL = "AWAITING_APPROVAL",
}

export enum Talk2PayDocumentTypes {
  IDENTIFICATION_SELFIE = "IDENTIFICATION_SELFIE",
  IDENTIFICATION = "IDENTIFICATION",
}

export interface Talk2PayDocument {
  status: Talk2PayStatus;
  type: Talk2PayDocumentTypes;
  title: string;
  description: string;
  onboardingUrl: string;
  onboardingQrCode: string;
}

export interface AccountBalance {
  balance: number;
}

export interface PlatformAccount {
  object: string;
  id: string;
  name: string;
  email: string;
  loginEmail: string;
  mobilePhone: string;
  address: string;
  addressNumber: string;
  complement: string;
  province: string;
  postalCode: string;
  cpfCnpj: string;
  personType: "FISICA" | "JURIDICA";
  companyType: CompanyType;
  city: number;
  state: string;
  country: string;
  walletId: string;
  apiKey: string;
  accountNumber: {
    agency: string;
    account: string;
    accountDigit: string;
  };
  incomeValue: number;
  incomeRange: "UP_TO_50K" | "UP_TO_100K" | "UP_TO_500K" | "ABOVE_500K";
}

export interface AccountAnticipationConfiguration {
  creditCardAutomaticEnabled: boolean;
}

export enum AccountStatus {
  Approved = "APPROVED",
  Rejected = "REJECTED",
  AwaitingApproval = "AWAITING_APPROVAL",
  Pending = "PENDING",
}

export interface AccountStatusResponse {
  id: string;
  commercialInfo: AccountStatus;
  bankAccountInfo: AccountStatus;
  documentation: AccountStatus;
  general: AccountStatus;
}

export interface AccountFees {
  payment: {
    bankSlip: {
      defaultValue: number;
      discountValue: number | null;
      expirationDate: string | null;
      daysToReceive: number;
    };
    creditCard: {
      operationValue: number;
      oneInstallmentPercentage: number;
      upToSixInstallmentsPercentage: number;
      upToTwelveInstallmentsPercentage: number;
      discountOneInstallmentPercentage: number;
      discountUpToSixInstallmentsPercentage: number;
      discountUpToTwelveInstallmentsPercentage: number;
      hasValidDiscount: boolean;
      daysToReceive: number;
      discountExpiration: string;
    };
    debitCard: {
      operationValue: number;
      defaultPercentage: number;
      daysToReceive: number;
    };
    pix: {
      fixedFeeValue: number;
      fixedFeeValueWithDiscount: number | null;
      percentageFee: number | null;
      minimumFeeValue: number | null;
      maximumFeeValue: number | null;
      discountExpiration: string | null;
      type: string;
      monthlyCreditsWithoutFee: number;
      creditsReceivedOfCurrentMonth: number;
    };
  };
}

export interface Talk2PayFees {
  payment: {
    bankSlip: {
      defaultValue: number;
      discountValue: number | null;
      expirationDate: string | null;
      daysToReceive: number;
    };
    creditCard: {
      operationValue: number;
      oneInstallmentPercentage: number;
      upToSixInstallmentsPercentage: number;
      upToTwelveInstallmentsPercentage: number;
      discountOneInstallmentPercentage: number;
      discountUpToSixInstallmentsPercentage: number;
      discountUpToTwelveInstallmentsPercentage: number;
      hasValidDiscount: boolean;
      daysToReceive: number;
      discountExpiration: string;
    };
    debitCard: {
      operationValue: number;
      defaultPercentage: number;
      daysToReceive: number;
    };
    pix: {
      fixedFeeValue: number;
      fixedFeeValueWithDiscount: number | null;
      percentageFee: number | null;
      minimumFeeValue: number | null;
      maximumFeeValue: number | null;
      discountExpiration: string | null;
      type: string;
      monthlyCreditsWithoutFee: number;
      creditsReceivedOfCurrentMonth: number;
    };
  };
}
export enum AccountType {
  CORRENTE = "CORRENTE",
  POUPANCA = "POUPANCA",
}

export interface BankAccount extends BankAccountInput {
  id: string;
  rowDate: string;
}

export interface BankAccountInput {
  [key: string]: unknown;

  platformId: number;
  bankCode: number | null;
  bankName: string;
  agency?: string | null;
  agencyDigit?: string | null;
  number: string;
  digit?: string | null;
  name: string;
  type: AccountType;
}

export interface FinancialTransaction {
  previousBalance: number;
  finalBalance: number;
  transactions: Transaction[];
}

export interface Transaction {
  id: string;
  value: number;
  balance: number;
  type: string;
  date: Date;
  description: string;
  paymentId: string;
  transactionType: string;
}

export interface IBank {
  id: string;
  code: number;
  name: string;
  fullName: string;
  ispb: string;
}

export enum OperationType {
  TED = "TED",
  PIX = "PIX",
}

export interface PaymentPayload {
  platformId: number;
  beneficiaryAccountId: string | null;
  operationType: OperationType;
  pixAddressKey: string | null;
  pixAddressKeyType: string | null;
  description: string | null;
  scheduleDate: string | null;
  amount: string;
}

export interface FinancialTransactionBase64 {
  midiaId: number;
  name: string;
  extension: string;
  base64: string;
}

export type CommercialCompanyType = "MEI" | "LIMITED" | "INDIVIDUAL" | "ASSOCIATION";
export type TaxRegime = "MEI" | "NATIONAL_SIMPLE" | "NORMAL_REGIME" | "UNKNOWN";

export interface CommercialInfoInput {
  personType: "FISICA" | "JURIDICA";
  cpfCnpj: string;
  birthDate: string | null;
  companyType: CommercialCompanyType | null;
  companyName: string | null;
  incomeValue: number;
  taxRegime: TaxRegime | null;
  email: string;
  phone: string | null;
  mobilePhone: string | null;
  site: string | null;
  postalCode: string;
  address: string;
  addressNumber: string;
  complement: string | null;
  province: string;
}

export interface CommercialInfo extends CommercialInfoInput {
  responsibleName?: string | null;
  inscricaoEstadual?: string | null;
  name?: string | null;
  status?: string | null;
  denialReason?: string | null;
  city?: { name?: string; state?: string } | null;
  availableCompanyNames?: string[] | null;
}
